import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {AsyncValidator, ValidatorGenerator} from '../../utils';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors, Validators
} from '@angular/forms';
import {WidgetValidator} from '@pt/pt-models';
import {CheckUsernameService} from '@pt/api-client';

@Component({
  selector: 'pt-input-username',
  templateUrl: './input-username.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputUsernameComponent)
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => InputUsernameComponent),
      multi: true
    }
  ]
})
export class InputUsernameComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, AsyncValidator {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() placeholder: string;
  @Input() checkUsernameService: CheckUsernameService;
  @Input() help: string;
  @Input() error: string;// Da implementare

  public formGroup: FormGroup;
  isChanged = false;
  isRequired: boolean;
  inputId: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.validators) {
      this.setFormGroup(true);
    } else {
      this.setFormGroup(false);
    }
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputUsername_' + this.controlName;
  }

  writeValue(obj: any): void {
    this.formGroup.controls.value.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.formGroup?.controls) {
      if (isDisabled) {
        this.formGroup.controls.value.disable({emitEvent: false});
      } else {
        this.formGroup.controls.value.enable({emitEvent: false});
      }
    }
  }

  validate(c: AbstractControl): Observable<ValidationErrors | null> {
    // must return result wrapped in Observable
    return of(!this.formGroup.controls.value.errors ? null : {invalid: true});
  }

  showErrorMessageContainer() {
    return this.formGroup.controls.value?.invalid && (this.formGroup.controls.value?.dirty || this.formGroup.controls.value?.touched);
  }

  showErrorMessage() {
    return this.formGroup.controls.value?.errors?.invalidAsync && !this.formGroup.controls.value?.errors?.required;
  }

  private setFormGroup(changeValidator?: boolean) {
    this.destroy$.next(true);

    const validator = ValidatorGenerator.generateValidator(this.validators);
    const asyncValidator = this.checkUsernameService ? AsyncValidator.checkUsername(this.checkUsernameService) : null;
    this.isRequired = validator?.includes(Validators.required);

    this.formGroup = new FormGroup({
      value: new FormControl(null, validator, asyncValidator)
    });

    this.formGroup.statusChanges.pipe(
      debounceTime(50),
      takeUntil(this.destroy$),
      distinctUntilChanged()
    ).subscribe(() => {
      this.formGroup.updateValueAndValidity();
    });


    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      if (this.onChanged) {
        this.onChanged(t ? t.value : undefined);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });

    if (changeValidator) {
      this.isChanged = true;
      if (this.onChanged) {
        this.onChanged(this.formGroup.controls.value.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    }
  }
}
